import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Mail, Lock, Loader2, Stethoscope, CalendarHeart, MessageCircle } from "lucide-react";
import { clinicHelpLink } from "@/lib/constants";
import InstallPWA from "@/components/InstallPWA";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
    redirectUrl
  )}`;
}

export default function Login() {
  const { loginStaff } = useAuth();
  const [tab, setTab] = useState("paciente");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("djf_remember") || "null");
      if (saved?.email) {
        setEmail(saved.email);
        setPassword(saved.password || "");
        setRemember(true);
        setTab("personal");
      }
    } catch (error) {
      console.error("No se pudieron leer las credenciales guardadas:", error);
      localStorage.removeItem("djf_remember");
    }
  }, []);

  const handleStaff = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      await loginStaff(cleanEmail, password);
      if (remember) {
        localStorage.setItem("djf_remember", JSON.stringify({ email: cleanEmail, password }));
      } else {
        localStorage.removeItem("djf_remember");
      }
      toast.success("Bienvenido");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[44%] p-12 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(150deg, #2E6CA4 0%, #1F4E78 60%, #163a5c 100%)" }}>
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }} />
        <div className="absolute -left-20 bottom-10 w-72 h-72 rounded-full" style={{ background: "rgba(255,255,255,0.05)" }} />
        <div className="relative flex items-center gap-3">
          <Logo size={64} />
          <div>
            <p className="font-display text-2xl leading-tight">Dr. Jairo Franco</p>
            <p className="text-white/70 text-sm">Ginecólogo · Obstetra</p>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-5xl leading-[1.05] mb-5">
            Tu salud,<br />en las mejores manos.
          </h1>
          <p className="text-white/80 max-w-md leading-relaxed">
            Agenda tu cita en línea de forma rápida, privada y segura. Atención
            ginecológica y obstétrica con calidez y profesionalismo.
          </p>
        </div>
        <div className="relative flex gap-6 text-white/70 text-sm">
          <span className="flex items-center gap-2"><CalendarHeart size={18} /> Agenda 24/7</span>
          <span className="flex items-center gap-2"><Stethoscope size={18} /> Atención privada</span>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fadeup">
          <div className="lg:hidden flex flex-col items-center mb-8 text-center">
            <Logo size={92} className="mb-3" />
            <p className="font-display text-3xl text-[#1F4E78]">Dr. Jairo Franco</p>
            <p className="text-muted-foreground text-sm">Ginecólogo · Agenda tu cita</p>
          </div>

          <div className="card-surface p-7">
            <h2 className="font-display text-3xl text-[#1F4E78] mb-1">Iniciar sesión</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Elige cómo quieres ingresar
            </p>

            <div className="flex p-1 rounded-full bg-[#EDF3F9] mb-6" data-testid="login-tabs">
              <button
                data-testid="tab-paciente"
                onClick={() => setTab("paciente")}
                className={`flex-1 py-2 text-sm rounded-full font-semibold transition-all ${
                  tab === "paciente" ? "bg-white text-[#2E6CA4] shadow-sm" : "text-muted-foreground"
                }`}
              >
                Soy paciente
              </button>
              <button
                data-testid="tab-personal"
                onClick={() => setTab("personal")}
                className={`flex-1 py-2 text-sm rounded-full font-semibold transition-all ${
                  tab === "personal" ? "bg-white text-[#2E6CA4] shadow-sm" : "text-muted-foreground"
                }`}
              >
                Personal
              </button>
            </div>

            {tab === "paciente" ? (
              <div className="space-y-4">
                <button
                  data-testid="google-login-btn"
                  onClick={startGoogleLogin}
                  className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-full border border-[#dfe6ee] bg-white hover:bg-[#f7fafd] transition-colors font-semibold text-[#1F4E78]"
                >
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
                  Continuar con Google
                </button>
                <p className="text-xs text-center text-muted-foreground leading-relaxed">
                  Inicia sesión con tu cuenta de Google para reservar y gestionar
                  tus citas. Tus datos son privados y solo el personal médico
                  puede verlos.
                </p>
                <div className="relative py-1">
                  <div className="border-t border-border" />
                  <span className="absolute inset-0 flex justify-center -top-2.5">
                    <span className="bg-white px-3 text-[11px] text-muted-foreground">o</span>
                  </span>
                </div>
                <a
                  href={clinicHelpLink()}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="clinic-whatsapp-help"
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-full font-semibold text-white transition-colors"
                  style={{ background: "#25D366" }}
                >
                  <MessageCircle size={18} /> No tengo correo o requiero otro horario
                </a>
                <p className="text-[11px] text-center text-muted-foreground">
                  Te atendemos por WhatsApp para agendar tu cita.
                </p>
              </div>
            ) : (
              <form onSubmit={handleStaff} className="space-y-4" data-testid="staff-login-form">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">Correo</label>
                  <div className="relative">
                    <Mail size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      data-testid="staff-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="correo@clinica.com"
                      className="field pl-10"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">Contraseña</label>
                  <div className="relative">
                    <Lock size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      data-testid="staff-password-input"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="field pl-10"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-label">
                  <input
                    type="checkbox"
                    data-testid="remember-me-checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 accent-[#2E6CA4] rounded"
                  />
                  <span className="text-sm text-foreground">Recordar correo y contraseña</span>
                </label>
                <button
                  data-testid="staff-login-submit"
                  type="submit"
                  disabled={busy}
                  className="btn-primary w-full py-3 flex items-center justify-center gap-2"
                >
                  {busy ? <Loader2 size={18} className="animate-spin" /> : null}
                  Ingresar
                </button>
                <p className="text-xs text-center text-muted-foreground">
                  Acceso exclusivo para médico y recepción.
                </p>
              </form>
            )}
          </div>
          <div className="mt-4">
            <InstallPWA variant="solid" />
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">
            © {new Date().getFullYear()} Consultorio Dr. Jairo Franco
          </p>
        </div>
      </div>
    </div>
  );
}
